import { LegacyCard, Checkbox, FormLayout, Layout, Page, PageActions, Select, TextField, InlineGrid } from '@shopify/polaris';
import React, { useState, useCallback, useEffect } from 'react';
import { ColorPickerCustom } from './ColorPickerCustom';
import { SkeletonPageCustom } from './SkeletonPageCustom';
import { Preview } from './Preview';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function Theme(props) {
    const navigate = useHostNavigate();

    const [loading, setLoading] = useState(true);
    const [initState, setInitState] = useState({});
    const [model, setModel] = useState({});
    const [processing, setProcessing] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');

    const fetch = FetchCustom();

    const populateData = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/theme');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setInitState(data);
            setModel(data);
            setLoading(false);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const saveChanges = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/customizeTheme', model);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setInitState(JSON.parse(JSON.stringify(model)));
            props.setIsDirty(false);
            props.changeToastActive(true);
        }
    };

    const discardChanges = async () => {
        setModel(Object.assign({}, JSON.parse(JSON.stringify(initState))));
        props.setIsDirty(false);
    };

    const revertDefaultTheme = async () => {
        setProcessing(true);
        props.setIsLoading(true);
        const data = await fetch('api/home/getDefaultTheme');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setModel(data);
            setLoading(false);
            setProcessing(false);
        }
    };

    const handleChange = useCallback(
        (fieldName, newValue) => {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model[fieldName] = newValue;
                return model;
            });
        },
        [],
    );

    useEffect(() => {
        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (JSON.stringify(props.user) !== '{}') {
            document.title = "Main Theme | Stack Discounts";
            populateData();
        }
        // eslint-disable-next-line
    }, [props.user]);

    useEffect(() => {
        if (JSON.stringify(model) !== '{}') {
            props.setIsDirty(JSON.stringify(initState) !== JSON.stringify(model));
        }
        // eslint-disable-next-line
    }, [model, initState]);

    useEffect(() => {
        if (props.triggerSave) {
            props.setTriggerSave(false);
            saveChanges();
        }
        // eslint-disable-next-line
    }, [props.triggerSave]);

    useEffect(() => {
        if (props.triggerDiscard) {
            props.setTriggerDiscard(false);
            discardChanges();
        }
        // eslint-disable-next-line
    }, [props.triggerDiscard]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    return (
        loading ?
            <SkeletonPageCustom />
            :
            <Page title='Main Theme'
                secondaryActions={[{ content: 'Revert defaults', loading: processing, onAction: () => { revertDefaultTheme() } }]}>
                <Layout>
                    <Layout.AnnotatedSection
                        title="Launcher"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Background color"
                                        fieldName="launcherBackgroundColor"
                                        value={model.launcherBackgroundColor}
                                        handleChange={handleChange}
                                    />
                                    <ColorPickerCustom
                                        label="Icon color"
                                        fieldName="launcherIconColor"
                                        value={model.launcherIconColor}
                                        handleChange={handleChange}
                                    />
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <Checkbox
                                        label="Show text on launcher"
                                        checked={model.launcherShowText}
                                        onChange={(newValue) => handleChange('launcherShowText', newValue)}
                                    />
                                    <TextField
                                        value={model.launcherText}
                                        onChange={(newValue) => handleChange('launcherText', newValue)}
                                        label="Launcher text"
                                        disabled={!model.launcherShowText}
                                    />
                                </InlineGrid>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Widget and header"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Widget background color"
                                        fieldName="widgetBackgroundColor"
                                        value={model.widgetBackgroundColor}
                                        handleChange={handleChange}
                                    />
                                    <ColorPickerCustom
                                        label="Close icon color (mobile)"
                                        fieldName="closeIconColor"
                                        value={model.closeIconColor}
                                        handleChange={handleChange}
                                    />
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Header background color"
                                        fieldName="headerBackgroundColor"
                                        value={model.headerBackgroundColor}
                                        handleChange={handleChange}
                                    />
                                    <ColorPickerCustom
                                        label="Header text color"
                                        fieldName="headerTextColor"
                                        value={model.headerTextColor}
                                        handleChange={handleChange}
                                    />
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <TextField
                                        value={model.headerText}
                                        onChange={(newValue) => handleChange('headerText', newValue)}
                                        label="Header text"
                                        helpText="This text will also be used as a combined title for all active automatic campaigns."
                                    />
                                    <div></div>
                                </InlineGrid>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Campaigns and discounts"
                    >
                        <LegacyCard>
                            <LegacyCard.Section>
                                <FormLayout>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <ColorPickerCustom
                                            label="Campaign background color"
                                            fieldName="campaignBackgroundColor"
                                            value={model.campaignBackgroundColor}
                                            handleChange={handleChange}
                                        />
                                        <Select
                                            label="Color mode for texts"
                                            options={[{ label: 'Dark', value: 'dark' }, { label: 'Light', value: 'light' }]}
                                            value={model.campaignTextColorMode}
                                            onChange={(newValue) => handleChange('campaignTextColorMode', newValue)}
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <ColorPickerCustom
                                            label="Discount border color"
                                            fieldName="discountBorderColor"
                                            value={model.discountBorderColor}
                                            handleChange={handleChange}
                                        />
                                        <ColorPickerCustom
                                            label="Applied discount border color"
                                            fieldName="discountAppliedBorderColor"
                                            value={model.discountAppliedBorderColor}
                                            handleChange={handleChange}
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <ColorPickerCustom
                                            label="Product/collection image background color"
                                            fieldName="itemImageBackgroundColor"
                                            value={model.itemImageBackgroundColor}
                                            handleChange={handleChange}
                                        />
                                        <div></div>
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <ColorPickerCustom
                                            label="Add to cart background color"
                                            fieldName="atcBackgroundColor"
                                            value={model.atcBackgroundColor}
                                            handleChange={handleChange}
                                        />
                                        <ColorPickerCustom
                                            label="Add to cart text color"
                                            fieldName="atcTextColor"
                                            value={model.atcTextColor}
                                            handleChange={handleChange}
                                        />
                                    </InlineGrid>
                                </FormLayout>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Campaign code"
                    >
                        <LegacyCard>
                            <LegacyCard.Section>
                                <FormLayout>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <ColorPickerCustom
                                            label="Code input background color"
                                            fieldName="campaignCodeInputBackgroundColor"
                                            value={model.campaignCodeInputBackgroundColor}
                                            handleChange={handleChange}
                                        />
                                        <ColorPickerCustom
                                            label="Code input text color"
                                            fieldName="campaignCodeInputTextColor"
                                            value={model.campaignCodeInputTextColor}
                                            handleChange={handleChange}
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <ColorPickerCustom
                                            label="Apply button background color"
                                            fieldName="applyButtonBackgroundColor"
                                            value={model.applyButtonBackgroundColor}
                                            handleChange={handleChange}
                                        />
                                        <ColorPickerCustom
                                            label="Apply button text color"
                                            fieldName="applyButtonTextColor"
                                            value={model.applyButtonTextColor}
                                            handleChange={handleChange}
                                        />
                                    </InlineGrid>
                                </FormLayout>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Message colors"
                    >
                        <LegacyCard>
                            <LegacyCard.Section>
                                <FormLayout>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <ColorPickerCustom
                                            label="Message background color"
                                            fieldName="messageBackgroundColor"
                                            value={model.messageBackgroundColor}
                                            handleChange={handleChange}
                                        />
                                        <ColorPickerCustom
                                            label="Message text color"
                                            fieldName="messageTextColor"
                                            value={model.messageTextColor}
                                            handleChange={handleChange}
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <ColorPickerCustom
                                            label="Message success icon color"
                                            fieldName="messageSuccessIconColor"
                                            value={model.messageSuccessIconColor}
                                            handleChange={handleChange}
                                        />
                                        <ColorPickerCustom
                                            label="Message error icon color"
                                            fieldName="messageErrorIconColor"
                                            value={model.messageErrorIconColor}
                                            handleChange={handleChange}
                                        />
                                    </InlineGrid>
                                </FormLayout>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Requirement progress"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Background color"
                                        fieldName="awayProgressBackgroundColor"
                                        value={model.awayProgressBackgroundColor}
                                        handleChange={handleChange}
                                    />
                                    <div></div>
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Gradient from color"
                                        fieldName="awayProgressGradientFromColor"
                                        value={model.awayProgressGradientFromColor}
                                        handleChange={handleChange}
                                    />
                                    <ColorPickerCustom
                                        label="Gradient to color"
                                        fieldName="awayProgressGradientToColor"
                                        value={model.awayProgressGradientToColor}
                                        handleChange={handleChange}
                                    />
                                </InlineGrid>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Footer"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Background color"
                                        fieldName="footerBackgroundColor"
                                        value={model.footerBackgroundColor}
                                        handleChange={handleChange}
                                    />
                                    <div></div>
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Discount background color"
                                        fieldName="footerDiscountBackgroundColor"
                                        value={model.footerDiscountBackgroundColor}
                                        handleChange={handleChange}
                                    />
                                    <ColorPickerCustom
                                        label="Discount text color"
                                        fieldName="footerDiscountTextColor"
                                        value={model.footerDiscountTextColor}
                                        handleChange={handleChange}
                                    />
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Checkout background color"
                                        fieldName="footerCheckoutBackgroundColor"
                                        value={model.footerCheckoutBackgroundColor}
                                        handleChange={handleChange}
                                    />
                                    <ColorPickerCustom
                                        label="Checkout text color"
                                        fieldName="footerCheckoutTextColor"
                                        value={model.footerCheckoutTextColor}
                                        handleChange={handleChange}
                                    />
                                </InlineGrid>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.Section>
                        <PageActions primaryAction={{
                            content: 'Save', loading: props.isLoading, onAction: () => {
                                if (props.user.viewOnly) return;
                                saveChanges();
                            }
                        }} />
                    </Layout.Section>
                </Layout>
                <Preview settings={model} user={props.user} />
            </Page >
    );
}